import { t, plural } from '@lingui/macro'
import { find } from 'lodash'

import { i18n } from 'utils/i18n'
import { filterOnlyNumbers } from 'utils/number'
import { calcDiscountedPrice } from 'utils/math'
import { getDateFromUTCSecondsFromEpoch } from 'utils/date-time'

const period1Week = Math.round(7 * 24 * 60 * 60)
const period1Month = Math.round(4.348125 * period1Week)
const period1Year = Math.round(12 * period1Month)

export const is1YearPlan = (plan: Partial<EF.Plan>) => {
  return plan.period === period1Year || plan.period_name === '1.year'
}

export const hasTrialPeriod = (plan: Partial<EF.Plan>) => {
  return (plan.trial_period_days && plan.trial_period_days > 0) || false
}

const parsePeriodName = (
  periodName?: string
): [number, 'year' | 'month' | 'week'] => {
  const res = (periodName || '').match(/(\d+)\.(year|month|week)s?/)
  if (res) {
    const [, quantity, unit] = res
    return [parseInt(quantity), unit as 'year' | 'month' | 'week']
  }
  return [1, 'year'] // fallback
}

export const getPeriodFromPlan = (plan: Partial<EF.Plan>) => {
  if (plan.period) return plan.period
  const [quantity, unit] = parsePeriodName(plan.period_name)
  switch (unit) {
    case 'year':
      return quantity * period1Year
    case 'month':
      return quantity * period1Month
    case 'week':
      return quantity * period1Week
  }
}

export const getYearlyPrice = (plan: EF.Plan) => {
  const priceAmount = filterOnlyNumbers(plan.device_amount_display)

  const period = getPeriodFromPlan(plan)
  const monthCount = period / period1Month
  const factor = Math.round(12 / monthCount)

  return factor * priceAmount
}

export const getPriceInformation = (
  plan: EF.Plan,
  discountInformation?: EF.DiscountInformation
) => {
  const basePrice = filterOnlyNumbers(plan.device_amount_display)

  let discountPrice
  if (discountInformation?.percent_off) {
    discountPrice = calcDiscountedPrice(
      basePrice,
      discountInformation.percent_off
    )
  }

  return [basePrice, discountPrice] as [number, number | undefined]
}

export const periodNameYear = (count: number) =>
  i18n._(plural({ value: count, one: '# year', other: '# years' }))

export const periodNameMonth = (count: number) =>
  i18n._(plural({ value: count, one: '# month', other: '# months' }))

export const getRenewalPeriod = (): { [key: string]: string } => ({
  '1.year': periodNameYear(1),
  '6.months': periodNameMonth(6),
  '3.months': periodNameMonth(3),
  '1.month': periodNameMonth(1),
})

export const getFrequency = (): { [key: string]: string } => ({
  '1.year': i18n._(t`yearly`),
  '6.months': periodNameMonth(6),
  '3.months': i18n._(t`quarterly`),
  '1.month': periodNameMonth(1),
})

export const convertPeriodToMonthCount = (period: number) =>
  Math.round(period / period1Month)

export const getPlanNameWithPriceAndFrequency = (
  periodName: string,
  price: string
) => {
  const [count, unit] = periodName.split('.')
  const planName =
    unit === 'year'
      ? i18n._(t`1-year plan`)
      : i18n._(
          plural({
            value: Number(count),
            one: '#-month plan',
            other: '#-months plan',
          })
        )

  return `${planName} – ${price}/${getFrequency()[periodName]}`
}

export const getYearlySavingAmount = (plan: EF.Plan, linkedPlan: EF.Plan) => {
  return Math.floor(Math.abs(getYearlyPrice(plan) - getYearlyPrice(linkedPlan)))
}

export const getRenewalDateFrom = (fromDate: Date, periodName: string) => {
  const payingPeriodInMonths = periodName.includes('year')
    ? 12
    : Number(periodName.split('.')[0])
  const date = new Date(fromDate.getTime())
  date.setMonth(date.getMonth() + payingPeriodInMonths)
  return date.toLocaleDateString()
}

const ensurePlan = <T extends EF.Plan>(val: unknown) => {
  if (
    val &&
    typeof val === 'object' &&
    [
      'identifier',
      'device_amount_display',
      'external_identifier',
    ].every((prop) => val?.hasOwnProperty(prop))
  ) {
    return val as T
  }
  return null
}

export const getPlan = <T extends EF.Plan>(plans: T[]) => (
  periodName = '1.year',
  deviceCurrency = 'USD'
): T => {
  return (
    ensurePlan(
      find(plans, {
        period_name: periodName,
        device_currency: deviceCurrency,
      })
    ) ||
    (ensurePlan(
      find(plans, {
        period_name: periodName,
        device_currency: 'USD',
      })
    ) as T)
  )
}

export const isSubscriptionExpired = (subscription: EF.Subscription) =>
  // A subscription might have a status of 'active' but the ends_at timestamp
  // is in the past
  subscription.status === 'expired' || subscription.ends_at < Date.now() / 1000

export const calcSubscriptionStartDate = (
  currentSubscription?: Pick<EF.Subscription, 'ends_at'>
) => {
  const now = new Date()
  if (currentSubscription?.ends_at) {
    const endDate = getDateFromUTCSecondsFromEpoch(currentSubscription.ends_at)
    if (endDate > now) {
      return endDate
    }
  }
  return now
}
